<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useSchema } from '../utils/useSchema';
import { useSEO } from '../utils/useSEO';
import { useOpenGraph } from '../utils/useOpenGraph';

const emit = defineEmits(['buy']);

const { addEventSchema, addProductSchema } = useSchema();
const { setPageSEO } = useSEO();
const { updateSocialMedia } = useOpenGraph();

const LAZO_RAFFLE = {
  id: 'R_GRAN_RIFA_150_MAUTES', 
  title: 'El Lazo Ganadero',
  description: 'Gran Sorteo El Lazo Ganadero. 150 Mautes a repartir. ¡Premios al mayor comprador!',
  ticketPrice: 20,
  currency: 'USD',
  total_tickets: 10000,
  start_at: '2026-01-18T00:00:00Z', 
  end_at: '2026-03-01T20:00:00Z', 
  images: ['https://media.lafavoritarifas.com/web/el_lazo_ganadero.webp'], 
  allowManualSelection: true,
  ticketBundles: [
    { qty: 11, price: 200 }
  ]
};

const ranking = ref<any[]>([]);

const fetchRanking = async () => {
    try {
        const API_BASE = import.meta.env.VITE_API_URL || 'https://raffle-api.diegobinance11.workers.dev';
        const res = await fetch(`${API_BASE}/public/raffles/${LAZO_RAFFLE.id}/ranking`);
        const data = await res.json();
        if (data.ok) {
            ranking.value = data.ranking;
        }
    } catch (e) {
        console.error('Failed to load ranking', e);
    }
};

onMounted(() => {
  // Advanced SEO & Tracking
  setPageSEO('lazo_ganadero');
  updateSocialMedia({
    title: 'El Lazo Ganadero - 150 Mautes - Rifas La Favorita',
    description: 'Participa por 150 Mautes. 11 Tickets por 200 Lechugas. El mejor sorteo ganadero de Venezuela.',
    ogImage: LAZO_RAFFLE.images[0],
    ogUrl: window.location.href
  });

  // Schema LD
  addProductSchema({
    name: LAZO_RAFFLE.title,
    description: LAZO_RAFFLE.description,
    image: LAZO_RAFFLE.images[0],
    offers: {
      price: 20.00,
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock'
    }
  }, 1);

  addEventSchema({
    name: LAZO_RAFFLE.title,
    startDate: LAZO_RAFFLE.start_at,
    endDate: LAZO_RAFFLE.end_at,
    description: LAZO_RAFFLE.description,
    image: LAZO_RAFFLE.images[0],
    offers: {
      price: 20.00,
      currency: 'USD',
      url: window.location.href
    }
  });

  fetchRanking();
});

  // Slider Logic
  const heroImages = [
    '/images/hero_lazo_slider_1.png',
    '/images/hero_lazo_slider_2.png',
    '/images/hero_lazo_slider_3.png'
  ];
  const currentHeroIndex = ref(0);

  setInterval(() => {
    currentHeroIndex.value = (currentHeroIndex.value + 1) % heroImages.length;
  }, 5000);

  const handleBuy = (qty: number) => {
  // Emit event to parent to start purchase flow with this specific raffle data
  emit('buy', {
    raffle: LAZO_RAFFLE,
    quantity: qty
  });
};
</script>

<template>
  <div class="relative min-h-screen bg-black text-white font-sans overflow-hidden">
    <!-- Hero Slider Background -->
    <div class="absolute inset-0 z-0">
      <transition-group name="fade" tag="div" class="relative w-full h-full">
        <div
          v-for="(img, index) in heroImages" 
          :key="img"
          v-show="currentHeroIndex === index"
          class="absolute inset-0 w-full h-full"
        >
          <img 
            :src="img" 
            alt="Fondo El Lazo Ganadero" 
            class="w-full h-full object-cover opacity-50 blur-sm scale-105 animate-pulse-slow transition-transform duration-[10000ms] ease-linear transform hover:scale-110"
          />
        </div>
      </transition-group>
      <div class="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-green-950/20"></div>
    </div>

    <div class="relative z-10 max-w-7xl mx-auto px-4 py-12 md:py-20 flex flex-col items-center">
      
      <!-- Logo / Header -->
      <img src="/images/logo.png" alt="Rifas La Favorita" class="w-32 mb-8 drop-shadow-lg" />

      <!-- Main Content Grid -->
      <div class="grid lg:grid-cols-2 gap-12 items-center w-full">
        
        <!-- Left: Image & Badge -->
        <div class="relative animate-in slide-in-from-left duration-700">
             <div class="relative group">
                <div class="absolute -inset-1 bg-gradient-to-r from-green-600 to-yellow-500 rounded-[2rem] blur opacity-75 group-hover:opacity-100 transition duration-1000 group-hover:duration-200"></div>
                <!-- Main Product Image (Lasso) -->
                <img 
                  src="https://media.lafavoritarifas.com/web/el_lazo_ganadero.webp" 
                  alt="El Lazo Ganadero - 150 Mautes" 
                  class="relative w-full rounded-[2rem] shadow-2xl border-4 border-green-500/30 transform hover:scale-[1.01] transition-all duration-500 z-10"
                />
                <!-- Floating Abstract Element -->
                <img 
                  src="/images/hero_lazo_3.png"
                  alt="Abstract Elements"
                  class="absolute -bottom-20 -left-20 w-48 h-48 opacity-80 animate-float z-20 pointer-events-none mix-blend-screen"
                />
          </div>


        </div>
        <!-- Right: Sales Copy & Actions -->
        <div class="text-center lg:text-left space-y-6 animate-in slide-in-from-right duration-700 delay-100 bg-black/40 p-6 rounded-3xl backdrop-blur-sm border border-white/5">
          
          <!-- Logos Section (Trust) -->
          <div class="flex items-center justify-center lg:justify-start gap-4 mb-2 opacity-90">
             <!-- Super Gana Text/Logo representation -->
             <div class="flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full">
               <span class="text-yellow-400 text-lg">⭐</span>
               <span class="font-bold text-white text-sm">CERTIFICADO POR SUPER GANA</span>
             </div>
          </div>

          <div>
            <h1 class="text-4xl md:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-green-400 to-yellow-500 leading-none drop-shadow-sm mb-2">
              EL LAZO <br/> GANADERO
            </h1>
            <h2 class="text-3xl md:text-5xl font-black text-white drop-shadow-lg mb-4">
              <span class="text-gold-500">150 MAUTES</span> A REPARTIR
            </h2>
            <p class="text-lg text-gray-300 font-medium leading-tight">
              Oportunidad única para ganar ganado de primera calidad.
              ¡Premios sorpresa al mayor comprador!
            </p>
          </div>

          <!-- Pricing Cards -->
          <div class="grid grid-cols-2 gap-3">
             <!-- Individual -->
             <div 
               @click="handleBuy(1)"
               class="cursor-pointer bg-white/5 border border-white/10 p-4 rounded-xl hover:bg-white/10 hover:border-green-500 transition-all group"
             >
                <div class="text-gray-400 text-[10px] font-bold uppercase tracking-widest mb-1">Individual</div>
                <div class="flex items-baseline gap-1">
                  <span class="text-3xl font-black text-white">20</span>
                  <span class="text-green-400 text-sm font-bold">Lechugas</span>
                </div>
             </div>

             <!-- Bundle -->
             <div 
               @click="handleBuy(11)"
               class="cursor-pointer bg-gradient-to-br from-green-900/80 to-emerald-900/80 border border-green-500 p-4 rounded-xl hover:scale-105 transition-all shadow-lg relative overflow-hidden"
             >
                <div class="absolute top-0 right-0 bg-yellow-500 text-black text-[10px] font-bold px-2 py-0.5 rounded-bl-lg">MEJOR VALOR</div>
                <div class="text-green-300 text-[10px] font-bold uppercase tracking-widest mb-1">Promo</div>
                <div class="flex items-baseline gap-1">
                  <span class="text-3xl font-black text-white">11</span>
                  <span class="text-white text-sm">x</span>
                  <span class="text-3xl font-black text-white">200</span>
                </div>
                <div class="text-green-200 text-sm font-bold">Lechugas</div>
             </div>
          </div>

          <!-- Purchase CTA -->
          <button 
            @click="handleBuy(11)"
            class="w-full py-4 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-400 hover:to-emerald-500 text-white font-black text-xl uppercase tracking-widest rounded-xl shadow-[0_0_20px_rgba(34,197,94,0.4)] transition-all hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2"
          >
            <span>COMPRAR PROMOCIÓN</span>
            <span class="text-2xl">🚀</span>
          </button>
            
          <!-- Trust Message -->
          <p class="text-[10px] text-gray-400 text-center leading-tight">
            🔒 Transacción segura. Sorteo certificado.
            <br/>Fecha: Se comunicará al tener el 60% de la venta.
          </p>

          <!-- Contact Numbers Grid (From Flyer) -->
          <div class="border-t border-white/10 pt-4 mt-2">
            <p class="text-[10px] uppercase text-gray-500 font-bold mb-2 text-center">Líneas de Atención / Ventas</p>
            <div class="flex flex-wrap justify-center gap-3">
               <a href="https://wa.me/584167452305" target="_blank" class="flex items-center gap-1.5 bg-green-500/10 px-3 py-1.5 rounded-full hover:bg-green-500/20 transition-colors border border-green-500/20">
                 <span class="text-green-400">📱</span> <span class="text-white font-mono text-xs">0416-7452305</span>
               </a>
               <a href="https://wa.me/584262750225" target="_blank" class="flex items-center gap-1.5 bg-green-500/10 px-3 py-1.5 rounded-full hover:bg-green-500/20 transition-colors border border-green-500/20">
                 <span class="text-green-400">📱</span> <span class="text-white font-mono text-xs">0426-2750225</span>
               </a>
               <a href="https://wa.me/584165424374" target="_blank" class="flex items-center gap-1.5 bg-green-500/10 px-3 py-1.5 rounded-full hover:bg-green-500/20 transition-colors border border-green-500/20">
                 <span class="text-green-400">📱</span> <span class="text-white font-mono text-xs">0416-5424374</span>
               </a>
            </div>
          </div>

        </div>
      </div>

    </div>
  </div>
</template>
